import {Alert} from 'src/design/atoms/Alert';
import {Box} from 'src/design/atoms/Box';
import {Group} from 'src/design/atoms/Group';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import {IconAlertTriangle, IconArrowLeft} from '@tabler/icons-react';
import {setUpTOTP, verifyTOTPSetup} from 'aws-amplify/auth';
import {useEffect, useState} from 'react';
import {Button} from 'src/design/atoms/Button';
import {Spinner} from 'src/design/atoms/Spinner';
import {CheckboxRow} from 'src/design/molecules/CheckboxRow';
import {TotpCodeForm} from 'src/app/security/authentication/TotpCodeForm';
import {TotpSetupDetails} from 'src/app/security/authentication/TotpSetupDetails';
import {ServerErrorAlert} from 'src/app/layout/ServerErrorAlert';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {useEnableOwnMfaMutation} from 'src/redux/queries/user';

export interface MfaSetupPanelProps {
  username: string;
  onCancel: () => void;
  onEnabled: () => void;
}

/** Two screens: acknowledge the magic-link impact, then scan and verify a code. */
export const MfaSetupPanel = ({
  username,
  onCancel,
  onEnabled,
}: MfaSetupPanelProps) => {
  const [screen, setScreen] = useState<'confirm' | 'setup'>('confirm');
  const [acknowledged, setAcknowledged] = useState(false);
  const [secret, setSecret] = useState<string>();
  const [verified, setVerified] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [enableOwnMfa] = useEnableOwnMfaMutation();

  const loadSecret = async () => {
    setBusy(true);
    setError(undefined);
    try {
      setSecret((await setUpTOTP()).sharedSecret);
    } catch (err) {
      setError(parseErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => {
    if (screen === 'setup' && !secret && !verified) void loadSecret();
  }, [screen, secret, verified]);

  const enable = async (code?: string) => {
    setBusy(true);
    setError(undefined);
    try {
      if (!verified && code) {
        await verifyTOTPSetup({code});
        setVerified(true);
        setSecret(undefined);
      }
      await enableOwnMfa().unwrap();
      onEnabled();
      return true;
    } catch (err) {
      setError(parseErrorMessage(err));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const cancelButton = (
    <Button variant="subtle" color="black" disabled={busy} onClick={onCancel}>
      Cancel
    </Button>
  );

  if (screen === 'confirm') {
    return (
      <Stack>
        <Alert
          variant="light"
          color="yellow"
          icon={<IconAlertTriangle />}
          title="Email sign-in links will stop working">
          After this, you will sign in with your password and a code from your
          authenticator app.
        </Alert>
        <CheckboxRow
          checked={acknowledged}
          label="I understand, and I know my password."
          onChange={(_, checked) => setAcknowledged(checked)}
        />
        <Group>
          <Button disabled={!acknowledged} onClick={() => setScreen('setup')}>
            Continue
          </Button>
          {cancelButton}
        </Group>
      </Stack>
    );
  }

  return (
    <Stack>
      <Button
        variant="subtle"
        color="black"
        size="sm"
        px={4}
        disabled={busy || verified}
        leftSection={<IconArrowLeft size={16} />}
        onClick={() => setScreen('confirm')}
        style={{alignSelf: 'flex-start'}}>
        Back
      </Button>
      <Stack gap={2}>
        <Text size="lg" fw={600}>
          Scan this code with your authenticator app
        </Text>
        <Text size="sm" c="dimmed">
          Then enter the 6-digit code the app shows for Proact.
        </Text>
      </Stack>
      {verified ? (
        <>
          <Text size="sm">Your code was verified. Select Turn on to finish.</Text>
          <ServerErrorAlert message={error} />
          <Group>
            <Button
              loading={busy}
              onClick={() => {
                void enable();
              }}>
              Turn on
            </Button>
            {cancelButton}
          </Group>
        </>
      ) : (
        <Group align="flex-start" gap="xl">
          {secret ? (
            <TotpSetupDetails sharedSecret={secret} username={username} />
          ) : (
            <Box w={200} py="md">
              <Spinner loadingText="Preparing your code..." />
            </Box>
          )}
          <Box style={{flex: 1, minWidth: 220}}>
            <TotpCodeForm
              loading={busy}
              label="Turn on"
              error={error}
              onSubmit={enable}
              secondaryActions={cancelButton}
            />
          </Box>
        </Group>
      )}
    </Stack>
  );
};
